import { signedFetch } from '../../super-picker/services/api';
import {
  getPublicShareUuid,
  getShareSessionToken,
  withShareUuid,
} from './shareContext';

/**
 * Single fetch wrapper used by every public Super Picker API call. Behaviour
 * depends on which mode the SPA is running in (see `shareContext.ts`):
 *
 *   - **Share-link recipient** (`uuid` known): we append `?uuid=...` to the
 *     URL so the backend's `requireShareAccess` middleware can authorize the
 *     call, and we do a plain `fetch` *without* `credentials` so we don't
 *     accidentally try to attach a stale agent cookie. We do **not** route
 *     through `signedFetch` because its 401 handler redirects to the SSO /
 *     unauthorized page — recipients have no SSO session to recover.
 *
 *   - **Agent EXTERNAL** (no uuid): we delegate to `signedFetch`, preserving
 *     the existing behaviour (cookie credentials, signature header, refresh
 *     retry, redirect on 401).
 */
export async function publicFetch(input: string, init?: RequestInit): Promise<Response> {
  const uuid = getPublicShareUuid();
  if (!uuid) {
    return signedFetch(input, init);
  }
  const url = withShareUuid(input);
  // Attach the share-session JWT issued by `verifyShareOtp` (see
  // `signShareSessionToken` on the backend). The backend's gate refuses
  // gated requests without it once the share has been activated, which
  // is what binds the link to the browser that completed the OTP flow.
  // We merge with any caller-supplied headers so callers can still set
  // `Content-Type` etc. without losing the bearer token.
  const headers = new Headers(init?.headers);
  const sessionToken = getShareSessionToken();
  if (sessionToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${sessionToken}`);
  }
  return fetch(url, {
    ...init,
    headers,
    // Recipients have no agent cookie; explicitly omit so a stale 3rd-party
    // cookie can't be sent (would still hit the unauth path on the backend).
    credentials: 'omit',
  });
}

/**
 * Build a download URL for routes used as `href` / `src` (no fetch wrapper).
 * Appends `?uuid=` when in recipient mode so the backend gate accepts it.
 */
export function publicAssetUrl(url: string): string {
  return withShareUuid(url);
}
